/**
 * TargetOutput — renders the target_employer ("Target Fit") step: an overall
 * recommendation badge + fit score, the per-requirement fit breakdown, hidden
 * requirements, the tailored positioning block, and objection handling Q&A.
 */

import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";
import { BulletSection, ScoreBar, SectionLabel } from "./shared";

export default function TargetOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};
  const requirements: any[] = o.requirements ?? [];
  const objections: any[] = o.objection_handling ?? [];
  const tp = o.tailored_positioning ?? {};

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {o.recommendation && (
          <Chip
            label={o.recommendation}
            sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.light, 0.12), color: theme.palette.primary.main }}
          />
        )}
        {typeof o.fit_score === "number" && (
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            {t("TargetOutput.fitScoreLabel")}{" "}
            <Typography component="span" fontWeight={500} sx={{ color: theme.palette.primary.main }}>
              {o.fit_score}
            </Typography>
          </Typography>
        )}
      </Stack>

      {requirements.length > 0 && (
        <Box>
          <SectionLabel>{t("TargetOutput.requirementFit")}</SectionLabel>
          <Stack spacing={1.25} mt={1}>
            {requirements.map((r, i) => (
              <ScoreBar
                key={`req-${i}`}
                label={r?.requirement ?? t("TargetOutput.requirementFallback", { index: i + 1 })}
                value={Number(r?.fit_score ?? 0)}
              />
            ))}
          </Stack>
        </Box>
      )}

      <BulletSection label={t("TargetOutput.hiddenRequirements")} items={o.hidden_requirements} />

      {(tp.headline || tp.summary || (tp.emphasis_bullets ?? []).length > 0 || (tp.talking_points ?? []).length > 0) && (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.light, 0.04) }}>
          <SectionLabel>{t("TargetOutput.tailoredPositioning")}</SectionLabel>
          {tp.headline && (
            <Typography variant="subtitle2" fontWeight={500} mt={0.75} sx={{ color: theme.palette.primary.main }}>
              {tp.headline}
            </Typography>
          )}
          {tp.summary && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {tp.summary}
            </Typography>
          )}
          <Stack spacing={1.5} mt={1.25}>
            <BulletSection label={t("TargetOutput.emphasize")} items={tp.emphasis_bullets} />
            <BulletSection label={t("TargetOutput.talkingPoints")} items={tp.talking_points} />
          </Stack>
        </Box>
      )}

      {objections.length > 0 && (
        <Box>
          <SectionLabel>{t("TargetOutput.objectionHandling")}</SectionLabel>
          <Stack spacing={1.5} mt={1}>
            {objections.map((ob, i) => (
              <Box key={`obj-${i}`}>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  {ob?.concern}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.25}>
                  {ob?.response}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <LessonMarkdown>{step.narrative_markdown ?? ""}</LessonMarkdown>
    </Stack>
  );
}
